const findLocationData = (): {
    l0?: { id: number };
    l1?: { id: number };
    l2?: { id: number };
    l3?: { id: number };
    l4?: { id: number };
} | null => {
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
        return null;
    }

    // Search through all items in dataLayer array to find the one with 'l' property
    // Location data is pushed to the array, so it might not be at index 0
    for (const item of window.dataLayer) {
        if (item?.l) {
            return item.l;
        }
    }

    return null;
};

const waitForLocationData = (
    timeoutMs = 3000
): Promise<{
    l0?: { id: number };
    l1?: { id: number };
    l2?: { id: number };
    l3?: { id: number };
    l4?: { id: number };
} | null> => {
    return new Promise((resolve) => {
        // Check immediately first
        const locationData = findLocationData();
        if (locationData) {
            resolve(locationData);
            return;
        }

        // If dataLayer.push is available, intercept it for immediate notification
        let resolved = false;
        const originalPush = window.dataLayer?.push;

        if (originalPush && typeof originalPush === 'function') {
            const checkAndResolve = (item: any) => {
                if (!resolved && item?.l) {
                    resolved = true;
                    // Restore original push
                    if (window.dataLayer && window.dataLayer.push !== originalPush) {
                        window.dataLayer.push = originalPush;
                    }
                    resolve(item.l);
                    return true;
                }
                return false;
            };

            // Intercept push to check new items immediately
            window.dataLayer.push = function (...args: any[]) {
                const result = originalPush.apply(this, args);

                // Check all newly pushed items
                for (const arg of args) {
                    if (checkAndResolve(arg)) {
                        return result;
                    }
                }

                return result;
            };
        }

        // Fallback: Exponential backoff polling
        // Check frequently at first (when data is most likely to arrive), then less frequently
        const startTime = Date.now();
        let pollCount = 0;
        const maxPollInterval = 500; // Max 500ms between checks
        const initialPollInterval = 50; // Start with 50ms

        const scheduleNextPoll = () => {
            if (resolved) {
                return;
            }

            const elapsed = Date.now() - startTime;
            if (elapsed >= timeoutMs) {
                resolved = true;
                // Restore original push if we intercepted it
                if (window.dataLayer && window.dataLayer.push !== originalPush && originalPush) {
                    window.dataLayer.push = originalPush;
                }
                resolve(null);
                return;
            }

            const locationData = findLocationData();
            if (locationData) {
                resolved = true;
                // Restore original push if we intercepted it
                if (window.dataLayer && window.dataLayer.push !== originalPush && originalPush) {
                    window.dataLayer.push = originalPush;
                }
                resolve(locationData);
                return;
            }

            // Exponential backoff: 50ms, 100ms, 200ms, then cap at 500ms
            const interval = Math.min(
                initialPollInterval * Math.pow(2, pollCount),
                maxPollInterval
            );
            pollCount++;
            setTimeout(scheduleNextPoll, interval);
        };

        // Start polling with initial interval
        setTimeout(scheduleNextPoll, initialPollInterval);
    });
};

export default async (googletag) => {
    const locationData = await waitForLocationData();

    if (!locationData) {
        return;
    }
    const l0LocationId = locationData.l0?.id ?? 'unknown';
    const l1LocationId = locationData.l1?.id ?? 'unknown';
    const l2LocationId = locationData.l2?.id ?? 'unknown';
    const l3LocationId = locationData.l3?.id ?? 'unknown';
    const l4LocationId = locationData.l4?.id ?? 'unknown';

    googletag.cmd.push(() => {
        googletag.pubads().setTargeting('Location_L0', `${l0LocationId}`);
        googletag.pubads().setTargeting('Location_L1', `${l1LocationId}`);
        googletag.pubads().setTargeting('Location_L2', `${l2LocationId}`);
        googletag.pubads().setTargeting('Location_L3', `${l3LocationId}`);
        googletag.pubads().setTargeting('Location_L4', `${l4LocationId}`);
    });
};
